/**
 * PlanStatusBanner — app-wide plan lifecycle notice.
 *
 * Renders directly under the header on every page when the plan that
 * governs the active workspace is not simply "active":
 *
 *   expiring_soon  amber  — non-auto-renewing plan ends within 7 days
 *   expired        red    — lapsed; org workspaces are read-only,
 *                           individual accounts run at Free-plan limits;
 *                           automations pause at ``grace_ends_at``
 *   suspended      red    — grace period passed; compute paused
 *   none           red    — no plan on file (deliberately deactivated)
 *
 * Source of truth is GET /v1/plans/current -> ``plan_state`` (built by
 * ``auth.plan.plan_state`` on the server, the same verdict the 403
 * envelope, the billing page and the ops console use), so the banner
 * can never disagree with what the API enforces.
 *
 * The banner re-checks whenever the active workspace changes (an org
 * workspace is governed by the org plan, a personal one by the user
 * plan) and whenever the billing snapshot reloads (after a plan change
 * / renewal), so it disappears as soon as the plan is fixed.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Stack, Typography, Button, IconButton, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import config from '../../config';
import { getLocalUser } from '../../utils/localUser';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const DISMISS_KEY = 'PLAN_BANNER_DISMISSED';

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/* Compact, action-oriented copy per state. The server ``message`` is
   the long form; here we keep one line + a detail line. */
const describe = (state) => {
  if (!state) return null;
  const name = state.name || 'current';
  const isOrg = state.effect === 'read_only' || (state.status === 'suspended' && state.effect !== 'degraded_to_free');
  const graceLabel = fmtDate(state.grace_ends_at);
  const expLabel = fmtDate(state.expires_at);

  switch (state.status) {
    case 'expiring_soon': {
      const days = state.days_left;
      const when = days == null ? `on ${expLabel}` : days <= 1 ? 'today' : `in ${days} days (${expLabel})`;
      const mode = state.billing_mode && !['paid', 'simulated', 'free'].includes(state.billing_mode)
        ? `${state.billing_mode} ` : '';
      return {
        tone: 'warning',
        title: `Your ${mode}${name} plan ends ${when} and will not renew automatically.`,
        detail: isOrg
          ? 'After that the organization becomes read-only and scheduled goals, pipelines and deployments pause.'
          : 'After that the account runs at Free-plan limits and resources above those limits pause.',
        cta: 'Renew plan',
        dismissible: true,
      };
    }
    case 'expired':
      return {
        tone: 'error',
        title: `Your ${name} plan expired on ${expLabel}.`,
        detail: isOrg
          ? `The organization is read-only until the plan is renewed${graceLabel ? `; automations will be paused on ${graceLabel}` : ''}.`
          : `The account runs at Free-plan limits until the plan is renewed${graceLabel ? `; resources above those limits will be paused on ${graceLabel}` : ''}.`,
        cta: 'Renew plan',
        dismissible: false,
      };
    case 'suspended':
      return {
        tone: 'error',
        title: `Your ${name} plan expired on ${expLabel} and the ${isOrg ? 'organization is suspended' : 'account has been moved to the Free plan'}.`,
        detail: isOrg
          ? 'Everything is read-only and automations are paused. Renew to restore full access — paused goals, pipelines and deployments resume automatically.'
          : 'Resources above the Free-plan limits are paused. Renew to restore your limits — they resume automatically.',
        cta: 'Renew plan',
        dismissible: false,
      };
    case 'none':
      return {
        tone: 'error',
        title: 'No active plan on this account.',
        detail: 'Choose a plan to continue using Predict.ai.',
        cta: 'Choose a plan',
        dismissible: false,
      };
    default:
      return null;
  }
};

const PlanStatusBanner = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeWorkspaceId, ready } = useWorkspace();
  // Any snapshot reload (plan change, renewal, top-up) re-checks state.
  const snapshotVersion = useSelector((s) => s.billing_snapshot?.response);
  const [state, setState] = useState(null);
  const [dismissedKey, setDismissedKey] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) || ''; } catch (_) { return ''; }
  });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${config.API_URL}/plans/current`, { headers: getLocalUser() });
      const body = res.data?.data ?? res.data;
      setState(body?.plan_state || null);
    } catch (_) {
      // A failed read must never block the app; the 403 handler and the
      // billing page still surface the problem.
      setState(null);
    }
  }, []);

  useEffect(() => {
    if (!ready) return undefined;
    load();
    return undefined;
  }, [ready, activeWorkspaceId, snapshotVersion, load]);

  if (!state) return null;
  const copy = describe(state);
  if (!copy) return null;

  // Dismissal is per (status, expiry) for the amber pre-expiry notice
  // only; red states always show.
  const key = `${state.status}:${state.expires_at || ''}`;
  if (copy.dismissible && dismissedKey === key) return null;

  const onBilling = location.pathname.startsWith('/settings/billing');
  const color = theme.palette[copy.tone]?.main || theme.palette.error.main;
  const Icon = copy.tone === 'warning' ? WarningAmberIcon : ErrorOutlineIcon;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, key); } catch (_) { /* ignore */ }
    setDismissedKey(key);
  };

  return (
    <Box
      role="status"
      data-testid="plan-status-banner"
      sx={{
        mt: 1.5, mb: 1, px: 1.5, py: 1,
        borderRadius: 1.5,
        border: `1px solid ${alpha(color, 0.45)}`,
        borderLeft: `3px solid ${color}`,
        backgroundColor: alpha(color, theme.palette.mode === 'dark' ? 0.12 : 0.06),
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
        <Icon sx={{ color, fontSize: 20, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {copy.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
            {copy.detail}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {!onBilling && (
            <Button
              size="small"
              variant="contained"
              color={copy.tone}
              onClick={() => navigate('/settings/billing?plans=1')}
              sx={{ height: 28, fontSize: 12, textTransform: 'none', boxShadow: 'none' }}
            >
              {copy.cta}
            </Button>
          )}
          {copy.dismissible && (
            <Tooltip title="Hide for this session" arrow>
              <IconButton size="small" onClick={dismiss} aria-label="Dismiss plan notice">
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default PlanStatusBanner;
